import { useState, useCallback } from 'react';
import {
  CANCELLED_POPUP_CODES,
  firebaseErrorCode,
  firebaseErrorMessage,
} from '@react-apps/auth';
import { extractApiErrorCode } from '@/lib/api-client';
import { authContainer } from '../../di/container';
import type {
  AuthMethod,
  FederatedMethod,
  MeResponse,
} from '../../domain/entities/auth.entity';

export const PROVIDER_NAME: Record<FederatedMethod, string> = {
  'google.com': 'Google',
  'microsoft.com': 'Microsoft',
};

/**
 * Vincular/desvincular formas de login do próprio usuário (google-sign-in §4.2).
 * O vínculo é client-side (`linkWithPopup`) e o backend só espelha; a remoção é
 * pela rota self-service, que exige reautenticação recente.
 */
export function useLoginMethods(onChanged: (me: MeResponse) => void) {
  const [isBusy, setIsBusy] = useState(false);
  /** Qual vínculo está em andamento — o botão dele diz "Vinculando…". */
  const [linking, setLinking] = useState<FederatedMethod | null>(null);
  const [error, setError] = useState<string | null>(null);

  const run = useCallback(
    async (
      action: () => Promise<MeResponse>,
      provider?: FederatedMethod,
    ): Promise<boolean> => {
      setIsBusy(true);
      setError(null);
      try {
        const me = await action();
        onChanged(me);
        // O popup vincula no Firebase, mas o back desfaz o vínculo cujo e-mail
        // não é o da conta (ou que a Microsoft entregou sem e-mail): o que vale
        // é a lista que voltou, não o popup ter fechado sem erro.
        if (provider && !me.authMethods.some((m) => m.method === provider)) {
          setError(
            `Não foi possível vincular: o e-mail da conta ${PROVIDER_NAME[provider]} escolhida não é o mesmo desta conta. Escolha outra conta ${PROVIDER_NAME[provider]} ou troque antes o e-mail da sua conta.`,
          );
          return false;
        }
        return true;
      } catch (err) {
        const fb = firebaseErrorCode(err);
        // Desistência do popup: fecha em silêncio, sem mensagem de erro.
        if (CANCELLED_POPUP_CODES.has(fb)) return false;
        if (fb === 'auth/credential-already-in-use') {
          // Só o vínculo chega aqui, e ele sempre diz de qual provedor é.
          setError(
            `Esta conta${provider ? ` ${PROVIDER_NAME[provider]}` : ''} já está vinculada a outro usuário.`,
          );
        } else if (
          fb === 'auth/wrong-password' ||
          fb === 'auth/invalid-credential'
        ) {
          setError('Senha atual incorreta.');
        } else if (fb === 'auth/too-many-requests') {
          setError(
            'Muitas tentativas. Aguarde alguns minutos e tente de novo.',
          );
        } else if (fb === 'auth/user-mismatch') {
          // Reautenticação pelo popup com outra conta: tentar de novo só ajuda
          // escolhendo a mesma.
          setError(
            'A conta escolhida não é a vinculada a este login. Escolha a mesma conta e tente de novo.',
          );
        } else if (fb === 'auth/provider-already-linked') {
          setError('Este método já está vinculado à sua conta.');
        } else if (extractApiErrorCode(err) === 'CANNOT_UNLINK_LAST_IDENTITY') {
          setError(
            'Esta é a sua única forma de acesso — vincule outra antes de removê-la.',
          );
        } else if (extractApiErrorCode(err) === 'REAUTHENTICATION_REQUIRED') {
          setError('Confirme sua identidade novamente e tente de novo.');
        } else {
          setError(
            firebaseErrorMessage(err) ??
              (err instanceof Error
                ? err.message
                : 'Não foi possível concluir a operação.'),
          );
        }
        return false;
      } finally {
        setIsBusy(false);
      }
    },
    [onChanged],
  );

  const link = useCallback(
    async (method: FederatedMethod) => {
      setLinking(method);
      try {
        return await run(
          () => authContainer.repository.linkFederated(method),
          method,
        );
      } finally {
        setLinking(null);
      }
    },
    [run],
  );

  /** `currentPassword` ausente ⇒ reautentica pelo popup do federado vinculado. */
  const unlink = useCallback(
    (method: AuthMethod, currentPassword?: string) =>
      run(() =>
        authContainer.repository.reauthenticateAndUnlinkIdentity(
          method,
          currentPassword,
        ),
      ),
    [run],
  );

  const clearError = useCallback(() => setError(null), []);

  return { link, unlink, isBusy, linking, error, clearError };
}
